import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Check,
  CheckCheck,
  ChevronDown,
  Gift,
  Image as ImageIcon,
  PackageMinus,
  Pencil,
  Plus,
  Store,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import { api, apiError, formatDate, money, moneyExact, todayISO } from "@/lib/api";
import { useConfirm } from "@/components/Confirm";
import { useSettings } from "@/context/SettingsContext";
import Layout from "@/components/Layout";
import SelectionBar from "@/components/SelectionBar";
import DateRange, { ALL_TIME, withinRange } from "@/components/DateRange";
import { useTenant } from "@/context/TenantContext";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";
import ItemPicker from "@/components/ItemPicker";
import { EmptyState, ItemCode, LoadingPanel } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

/** Add or replace a photo of the box itself. */
function BoxPhotoButton({ box, onChanged }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Hidden entirely when storage isn't set up, rather than offering
    // something that can only fail.
    api
      .get("/photos/ready")
      .then(({ data }) => setReady(!!data.ready))
      .catch(() => setReady(false));
  }, []);

  if (!ready) return null;

  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file) return;
          setBusy(true);
          try {
            const body = new FormData();
            body.append("file", file);
            await api.post(`/giftboxes/${box.build_id}/photo`, body);
            toast.success("Photo added");
            onChanged?.();
          } catch (err) {
            toast.error(apiError(err));
          } finally {
            setBusy(false);
          }
        }}
      />
      <Button size="sm" variant="outline" disabled={busy} onClick={() => input.current?.click()}>
        <ImageIcon className="w-3.5 h-3.5 mr-1.5" />
        {busy ? "Uploading…" : box.photo_url ? "Change photo" : "Add a photo"}
      </Button>
      {box.photo_url && (
        <Button
          size="sm"
          variant="ghost"
          className="text-muted2 hover:text-stock-out"
          onClick={async () => {
            try {
              await api.delete(`/giftboxes/${box.build_id}/photo`);
              toast.success("Photo removed");
              onChanged?.();
            } catch (err) {
              toast.error(apiError(err));
            }
          }}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      )}
    </>
  );
}

/** The box's own photo, four of its contents, or the icon — in that order. */
function BoxThumb({ box, className = "w-10 h-10" }) {
  if (box.photo_url) {
    return (
      <img
        src={box.photo_url}
        alt=""
        className={cn("rounded-xl object-cover shrink-0", className)}
      />
    );
  }
  const shots = box.collage || [];
  if (shots.length > 0) {
    return (
      <div className={cn("rounded-xl overflow-hidden grid grid-cols-2 shrink-0 bg-surface", className)}>
        {shots.slice(0, 4).map((src, i) => (
          <img key={i} src={src} alt="" className="w-full h-full object-cover" />
        ))}
      </div>
    );
  }
  return (
    <div className={cn("rounded-xl bg-brand-light flex items-center justify-center shrink-0", className)}>
      <Gift className="w-[18px] h-[18px] text-brand" />
    </div>
  );
}

function BoxCard({
  box,
  sym,
  onDelete,
  onEdit,
  onSell,
  onUnsell,
  onPhoto,
  shopEnabled,
  onToggleShop,
  selectable,
  selected,
  onToggleSelect,
}) {
  const [open, setOpen] = useState(false);
  const margin = box.margin ?? 0;
  return (
    <div className={cn("panel overflow-hidden", selected && "ring-1 ring-brand")}>
      <div
        className={cn(
          "flex items-center gap-3 px-5 py-4 transition-colors",
          !selected && "hover:bg-brand-light/30"
        )}
      >
        {selectable && (
          <button
            onClick={() => onToggleSelect(box.build_id)}
            aria-label={selected ? `Deselect ${box.box_type}` : `Select ${box.box_type}`}
            title="Select"
            className={cn(
              "w-[18px] h-[18px] rounded-full border shrink-0 flex items-center justify-center transition-colors",
              selected ? "bg-brand border-brand" : "border-line hover:border-brand-ring"
            )}
          >
            {selected && <Check className="w-3 h-3 text-white" />}
          </button>
        )}
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex-1 min-w-0 flex items-center gap-4 text-left"
        >
          <BoxThumb box={box} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-display text-[16px] font-semibold text-ink truncate">
                {box.box_type}
              </span>
              {box.sold && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-stock-in border border-emerald-200 px-2 py-0.5 text-[11px] shrink-0">
                  <Check className="w-3 h-3" /> Sold
                </span>
              )}
              {!box.sold && box.in_shop && (
                <span className="inline-flex items-center gap-1 rounded-full bg-stock-in/10 text-stock-in px-2 py-0.5 text-[11px] shrink-0">
                  <Store className="w-3 h-3" /> In shop
                </span>
              )}
            </div>
            <div className="text-[12.5px] text-muted2 mt-0.5">
              {formatDate(box.build_date)} · {box.lines?.length || 0} item
              {box.lines?.length === 1 ? "" : "s"} · {box.item_count} unit
              {box.item_count === 1 ? "" : "s"}
              {box.recipient ? ` · ${box.recipient}` : ""}
            </div>
          </div>
          <div className="text-right shrink-0 hidden sm:block">
            <div className="text-[12.5px] text-muted2">Contents cost</div>
            <div className="font-mono text-[14px] text-ink tnum">{money(box.cost, sym)}</div>
          </div>
          <div className="text-right shrink-0 hidden lg:block w-24">
            <div className="text-[12.5px] text-muted2">Sold for</div>
            <div className="font-mono text-[14px] text-ink tnum">
              {box.box_price ? money(box.box_price, sym) : "—"}
            </div>
          </div>
          <div className="text-right shrink-0 hidden md:block w-24">
            <div className="text-[12.5px] text-muted2">You made</div>
            <div
              className={cn(
                "font-mono text-[14px] tnum",
                // Amber, not red — pricing a box under cost is a choice, not an error.
                box.box_price ? (margin >= 0 ? "text-stock-in" : "text-stock-low") : "text-muted2"
              )}
            >
              {box.box_price ? money(margin, sym) : "—"}
            </div>
          </div>
          <ChevronDown
            className={cn(
              "w-4 h-4 text-muted2 shrink-0 transition-transform",
              open && "rotate-180"
            )}
          />
        </button>
      </div>

      {open && (
        <div className="border-t border-line bg-surface/50 px-5 py-4">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="eyebrow">What went in</div>
            <div className="ml-auto flex items-center gap-1.5">
              {box.sold ? (
                <Button size="sm" variant="outline" onClick={() => onUnsell(box)}>
                  <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Not sold after all
                </Button>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onEdit(box)}
                    title="Add or take out items"
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> Change contents
                  </Button>
                  <BoxPhotoButton box={box} onChanged={onPhoto} />
                  <Button
                    size="sm"
                    onClick={() => onSell(box)}
                    className="bg-brand hover:bg-brand-hover"
                  >
                    <Check className="w-3.5 h-3.5 mr-1.5" /> Mark sold
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(box)}
                    className="text-muted2 hover:text-stock-out"
                    title="Unpack and return everything"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            {box.lines?.map((l) => (
              <div
                key={l.entry_id}
                className="flex items-center gap-3 bg-white rounded-lg border border-line px-3.5 py-2.5"
              >
                <ItemCode code={l.item_code} />
                <span className="text-[13px] text-ink flex-1 truncate">{l.item_name}</span>
                <span className="font-mono text-[13px] text-muted2 tnum">×{l.quantity}</span>
                <span className="font-mono text-[13px] text-ink tnum w-20 text-right">
                  {money((l.unit_cost || 0) * l.quantity, sym)}
                </span>
              </div>
            ))}
          </div>
          {box.discount > 0 && (
            <div className="mt-3 flex items-center gap-2 text-[12.5px] text-muted2">
              <span>Sold separately {money(box.retail_total, sym)}</span>
              <span className="text-brand">−{money(box.discount, sym)} discount</span>
              <span className="text-ink">= {money(box.box_price, sym)}</span>
            </div>
          )}
          {box.notes && <p className="text-[12.5px] text-muted2 mt-3">{box.notes}</p>}

          {shopEnabled && !box.sold && (
            <div className="mt-3 rounded-xl border border-line bg-white p-3.5 flex items-start gap-3">
              <Switch
                checked={!!box.in_shop}
                onCheckedChange={(v) => onToggleShop(box, v)}
                className="mt-0.5 shrink-0"
              />
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-ink">Ready-made in the shop</div>
                <p className="text-[11.5px] text-muted2 mt-0.5 leading-relaxed">
                  Shows on the storefront's gift boxes doorway, one of a kind, until it
                  sells or you take it back out. Customers see its name and price —
                  never what it cost you.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SellBoxDialog({ box, sym, onClose, onConfirm }) {
  const [form, setForm] = useState({ customer: "", channel: "", sale_date: todayISO(), price: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (box) {
      setForm({
        customer: box.recipient || "",
        channel: "",
        sale_date: todayISO(),
        price: box.box_price ? String(box.box_price) : "",
      });
    }
  }, [box]);

  if (!box) return null;

  const price = Number(form.price) || 0;
  const profit = price - (box.cost || 0);

  const submit = async () => {
    if (!price) {
      toast.error("Give the box a price");
      return;
    }
    setBusy(true);
    await onConfirm({ ...form, price });
    setBusy(false);
  };

  return (
    <Dialog open={!!box} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Mark this box sold</DialogTitle>
          <DialogDescription>
            Records the money in Sales. Stock doesn't move — those items came off
            the shelf when the box was built.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="rounded-xl bg-brand-light px-4 py-3">
            <div className="text-[13.5px] font-medium text-brand">{box.box_type}</div>
            <div className="text-[12.5px] text-brand/80 mt-0.5">
              {box.item_count} unit{box.item_count === 1 ? "" : "s"} · cost{" "}
              {money(box.cost, sym)}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="sold-price">Paid</Label>
              <Input
                id="sold-price"
                type="number"
                min="0"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sold-date">Date</Label>
              <Input
                id="sold-date"
                type="date"
                value={form.sale_date}
                onChange={(e) => setForm({ ...form, sale_date: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="sold-customer">Customer</Label>
              <Input
                id="sold-customer"
                value={form.customer}
                onChange={(e) => setForm({ ...form, customer: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sold-channel">Channel</Label>
              <Input
                id="sold-channel"
                value={form.channel}
                onChange={(e) => setForm({ ...form, channel: e.target.value })}
                placeholder="Instagram, market…"
              />
            </div>
          </div>

          {price > 0 && (
            <div className="flex items-center justify-between text-[13px] rounded-lg border border-line px-3.5 py-2.5">
              <span className="text-muted2">You make</span>
              <span
                className={cn(
                  "font-mono font-semibold",
                  profit >= 0 ? "text-stock-in" : "text-stock-low"
                )}
              >
                {moneyExact(profit, sym)}
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover">
            Mark sold
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function GiftBoxes() {
  const confirm = useConfirm();
  const { settings, labelFor } = useSettings();
  const sym = settings.currency_symbol;
  const boxWord = labelFor("gift_boxes");
  const { tenantId } = useTenant();
  const CK = cacheKey("giftboxes", tenantId);
  const cached = cacheGet(CK);
  const [boxes, setBoxes] = useState(() => cached?.boxes || []);
  const [items, setItems] = useState(() => cached?.items || []);
  const [loading, setLoading] = useState(() => !cached);
  const [building, setBuilding] = useState(false);
  const [range, setRange] = useState(ALL_TIME);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [customType, setCustomType] = useState(false);

  // Ignore a reply that lands after the client has been switched — otherwise
  // one business's figures can appear under another's name.
  const currentKey = useRef(CK);
  useEffect(() => {
    currentKey.current = CK;
  }, [CK]);

  const load = useCallback(async () => {
    const askedFor = CK;
    if (!cacheGet(CK)) setLoading(true);
    try {
      const [b, i] = await Promise.all([api.get("/giftboxes"), api.get("/items")]);
      if (currentKey.current !== askedFor) return;
      setBoxes(b.data);
      setItems(i.data);
      cacheSet(CK, { boxes: b.data, items: i.data });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [CK]);

  useEffect(() => {
    load();
  }, [load]);

  const inStock = useMemo(() => items.filter((i) => i.current_stock > 0), [items]);

  // When editing, an item can be entirely inside this box and so show zero
  // stock left. It still belongs in the list — its units are allocated to this
  // box, not gone. Without this the picker can't find it and falls back to the
  // placeholder, which looks like the line cleared itself.
  const pickable = useMemo(() => {
    if (!form) return inStock;
    const chosen = new Set(form.lines.map((l) => l.item_code).filter(Boolean));
    const extras = items.filter(
      (i) => chosen.has(i.item_code) && !inStock.some((s) => s.item_code === i.item_code)
    );
    return extras.length ? [...inStock, ...extras] : inStock;
  }, [items, inStock, form]);

  const [editingId, setEditingId] = useState(null);
  const [selling, setSelling] = useState(null);

  // Editing opens the same builder, filled in. Only the difference in stock
  // moves when it's saved.
  const openEditor = (box) => {
    setCustomType(!(settings.gift_box_types || []).includes(box.box_type));
    setEditingId(box.build_id);
    setForm({
      box_type: box.box_type,
      build_date: box.build_date,
      discount: box.discount ? String(box.discount) : "",
      discountType: "amount",
      priceOverride: box.box_price ? String(box.box_price) : "",
      recipient: box.recipient || "",
      notes: box.notes || "",
      lines: (box.lines || []).map((l) => ({
        item_code: l.item_code,
        quantity: String(l.quantity),
      })),
    });
    setBuilding(true);
  };

  const openBuilder = () => {
    setCustomType(false);
    setEditingId(null);
    setForm({
      box_type: "",
      build_date: todayISO(),
      discount: "",
      discountType: "amount",
      priceOverride: "",
      recipient: "",
      notes: "",
      lines: [{ item_code: "", quantity: "1" }],
    });
    setBuilding(true);
  };

  const setLine = (idx, patch) =>
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    }));

  const addLine = () =>
    setForm((f) => ({ ...f, lines: [...f.lines, { item_code: "", quantity: "1" }] }));

  const removeLine = (idx) =>
    setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  // What the contents cost, and what they'd fetch sold individually. The
  // second is the natural starting point for a box price.
  const { contentsCost, contentsRetail } = useMemo(() => {
    if (!form) return { contentsCost: 0, contentsRetail: 0 };
    return form.lines.reduce(
      (totals, l) => {
        const item = items.find((i) => i.item_code === l.item_code);
        const qty = Number(l.quantity) || 0;
        return {
          contentsCost: totals.contentsCost + (item?.unit_cost || 0) * qty,
          contentsRetail: totals.contentsRetail + (item?.selling_price || 0) * qty,
        };
      },
      { contentsCost: 0, contentsRetail: 0 }
    );
  }, [form, items]);

  // Price is worked out from the discount unless someone types over it.
  const discountValue = useMemo(() => {
    if (!form) return 0;
    const raw = Number(form.discount) || 0;
    if (raw <= 0) return 0;
    return form.discountType === "percent"
      ? Math.min((contentsRetail * raw) / 100, contentsRetail)
      : Math.min(raw, contentsRetail);
  }, [form, contentsRetail]);

  const boxPrice = useMemo(() => {
    if (!form) return 0;
    if (form.priceOverride !== "") return Number(form.priceOverride) || 0;
    return Math.max(contentsRetail - discountValue, 0);
  }, [form, contentsRetail, discountValue]);

  const profit = boxPrice - contentsCost;
  const belowCost = boxPrice > 0 && profit < 0;

  const submit = async () => {
    if (!form.box_type.trim()) {
      toast.error("Give the box a type");
      return;
    }
    const lines = form.lines
      .filter((l) => l.item_code && Number(l.quantity) > 0)
      .map((l) => ({ item_code: l.item_code, quantity: Number(l.quantity) }));
    if (!lines.length) {
      toast.error("Add at least one item to the box");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        box_type: form.box_type,
        build_date: form.build_date,
        box_price: boxPrice,
        retail_total: contentsRetail,
        discount: discountValue,
        recipient: form.recipient,
        notes: form.notes,
        lines,
      };
      if (editingId) {
        await api.put(`/giftboxes/${editingId}`, payload);
        toast.success("Box updated — stock adjusted for the difference");
      } else {
        await api.post("/giftboxes", payload);
        toast.success("Gift box built — stock updated");
      }
      setBuilding(false);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const markSold = async (details) => {
    try {
      await api.post(`/giftboxes/${selling.build_id}/sell`, details);
      toast.success("Marked sold — the revenue is in Sales");
      setSelling(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const markUnsold = async (box) => {
    const ok = await confirm({
      title: "Mark this box as not sold?",
      body: "The sale it created will be removed. Stock isn't affected either way.",
      confirmText: "Undo the sale",
    });
    if (!ok) return;
    try {
      await api.post(`/giftboxes/${box.build_id}/unsell`);
      toast.success("Sale removed");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (box) => {
    const ok = await confirm({
      title: `Unpack this ${box.box_type}?`,
      body: `All ${box.item_count} unit${box.item_count === 1 ? "" : "s"} go back into stock.`,
      confirmText: "Unpack",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/giftboxes/${box.build_id}`);
      toast.success("Box unpacked — stock returned");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleShop = async (box, value) => {
    try {
      await api.put(`/giftboxes/${box.build_id}`, { in_shop: value });
      toast.success(value ? "Added to your shop" : "Taken out of your shop");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  // Boxes are cards rather than a table, so the range filters the list the
  // same way and the count follows it.
  const visible = useMemo(() => boxes.filter((b) => withinRange(b.build_date, range)), [boxes, range]);
  const totalUnits = visible.reduce((t, b) => t + (b.item_count || 0), 0);

  // Tick a few boxes and switch them into (or out of) the shop together,
  // instead of opening each one. A sold box is one of a kind and already
  // gone, so it never offers a checkbox.
  const [selected, setSelected] = useState([]);
  const selectableBoxes = useMemo(() => visible.filter((b) => !b.sold), [visible]);
  const toggleSelect = (id) =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const chosenBoxes = boxes.filter((b) => selected.includes(b.build_id));
  const alreadyInShop = chosenBoxes.filter((b) => b.in_shop).length;

  const bulkSetShop = async (value) => {
    try {
      const { data } = await api.post("/giftboxes/shop", {
        build_ids: selected,
        in_shop: value,
      });
      const n = data.updated.length;
      toast.success(
        value
          ? `${n} box${n === 1 ? "" : "es"} added to your shop`
          : `${n} box${n === 1 ? "" : "es"} taken out of your shop`
      );
      setSelected([]);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Layout
      title={boxWord}
      subtitle={
        // Held back until the data arrives — otherwise it briefly
        // describes an empty page.
        loading ? undefined : `${visible.length} built · ${totalUnits} units packed`
      }
      action={
        <>
          <DateRange value={range} onChange={setRange} />
          {settings.shop?.enabled && selectableBoxes.length > 1 && (
            <Button
              variant="outline"
              onClick={() =>
                setSelected((cur) =>
                  cur.length === selectableBoxes.length
                    ? []
                    : selectableBoxes.map((b) => b.build_id)
                )
              }
            >
              <CheckCheck className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">
                {selected.length === selectableBoxes.length ? "Select none" : "Select all"}
              </span>
            </Button>
          )}
          <Button onClick={openBuilder} className="bg-brand hover:bg-brand-hover">
          <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Build a box</span>
          </Button>
        </>
      }
    >
      <SelectionBar
        top="top-[68px]"
        count={selected.length}
        detail={
          alreadyInShop
            ? `${alreadyInShop} already in the shop`
            : "none in the shop yet"
        }
        onClear={() => setSelected([])}
        minimum={1}
        secondaryMinimum={1}
        secondaryLabel="Take out of shop"
        secondaryIcon={PackageMinus}
        onSecondary={() => bulkSetShop(false)}
        actionLabel="Add to shop"
        actionIcon={Store}
        onAction={() => bulkSetShop(true)}
      />

      {loading ? (
        <LoadingPanel rows={4} />
      ) : boxes.length === 0 ? (
        <div className="panel">
          <EmptyState
            icon={Gift}
            title={`No ${boxWord.toLowerCase()} yet`}
            body="Building a box takes each item out of stock at once, so your counts stay right without logging them one by one."
            action={
              <Button onClick={openBuilder} className="bg-brand hover:bg-brand-hover">
                <Plus className="w-4 h-4 mr-2" /> Build a box
              </Button>
            }
          />
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((b) => (
            <BoxCard
              key={b.build_id}
              box={b}
              sym={sym}
              onDelete={remove}
              onEdit={openEditor}
              onSell={setSelling}
              onUnsell={markUnsold}
              onPhoto={load}
              shopEnabled={settings.shop?.enabled}
              onToggleShop={toggleShop}
              selectable={settings.shop?.enabled && !b.sold}
              selected={selected.includes(b.build_id)}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      )}

      <SellBoxDialog
        box={selling}
        sym={sym}
        onClose={() => setSelling(null)}
        onConfirm={markSold}
      />

      <Dialog open={building} onOpenChange={setBuilding}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingId ? "Change what's in the box" : "Build a gift box"}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? "Only the difference moves — add a line and that much more comes out of stock, remove one and it goes back."
                : "Each line comes out of that item's stock when you save."}
            </DialogDescription>
          </DialogHeader>

          {form && (
            <div className="space-y-4 py-1">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="bt">Box type</Label>
                  {/* A datalist only suggests values matching what's already
                      typed, which hid every option but the first. A real
                      dropdown always shows the full list, with an escape hatch
                      for a one-off type. */}
                  {customType ? (
                    <div className="flex gap-2">
                      <Input
                        id="bt"
                        autoFocus
                        value={form.box_type}
                        onChange={(e) => setForm({ ...form, box_type: e.target.value })}
                        placeholder="Name this box type"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        title="Back to the list"
                        onClick={() => {
                          setCustomType(false);
                          setForm({
                            ...form,
                            box_type: "",
                          });
                        }}
                        className="shrink-0 text-muted2"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Select
                      value={form.box_type}
                      onValueChange={(v) => {
                        if (v === "__custom__") {
                          setCustomType(true);
                          setForm({ ...form, box_type: "" });
                        } else {
                          setForm({ ...form, box_type: v });
                        }
                      }}
                    >
                      <SelectTrigger id="bt">
                        <SelectValue placeholder="Choose a type" />
                      </SelectTrigger>
                      <SelectContent>
                        {(settings.gift_box_types || []).filter(Boolean).map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                        <SelectItem value="__custom__">
                          <span className="text-brand">Something else…</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="bd">Date</Label>
                  <Input
                    id="bd"
                    type="date"
                    value={form.build_date}
                    onChange={(e) => setForm({ ...form, build_date: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="br">For</Label>
                  <Input
                    id="br"
                    value={form.recipient}
                    onChange={(e) => setForm({ ...form, recipient: e.target.value })}
                    placeholder="Customer or occasion"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Contents</Label>
                  <button
                    onClick={addLine}
                    className="text-[12.5px] text-brand hover:text-brand-hover font-medium inline-flex items-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add item
                  </button>
                </div>
                <div className="space-y-2">
                  {form.lines.map((line, idx) => (
                    <div key={idx} className="flex gap-2 items-start">
                      <div className="flex-1 min-w-0">
                        <ItemPicker
                          items={pickable}
                          value={line.item_code}
                          onChange={(code) => setLine(idx, { item_code: code })}
                          placeholder="Choose an item"
                        />
                      </div>
                      <Input
                        type="number"
                        min="1"
                        value={line.quantity}
                        onChange={(e) => setLine(idx, { quantity: e.target.value })}
                        className="w-20 shrink-0"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLine(idx)}
                        disabled={form.lines.length === 1}
                        className="shrink-0 text-muted2 hover:text-stock-out"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-line overflow-hidden">
                <div className="px-4 py-3 space-y-2 bg-surface/60">
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-muted2">Contents sold separately</span>
                    <span className="font-mono text-ink">{moneyExact(contentsRetail, sym)}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-[13px] text-muted2 flex-1">Discount</span>
                    <div className="flex rounded-lg border border-line overflow-hidden">
                      {["amount", "percent"].map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setForm({ ...form, discountType: t })}
                          className={cn(
                            "px-2.5 py-1 text-[12px] transition-colors",
                            form.discountType === t
                              ? "bg-brand text-white"
                              : "bg-white text-muted2 hover:text-ink"
                          )}
                        >
                          {t === "amount" ? sym : "%"}
                        </button>
                      ))}
                    </div>
                    <Input
                      type="number"
                      min="0"
                      value={form.discount}
                      onChange={(e) =>
                        setForm({ ...form, discount: e.target.value, priceOverride: "" })
                      }
                      placeholder="0"
                      className="w-24 h-8 text-right"
                    />
                  </div>

                  {discountValue > 0 && (
                    <div className="flex items-center justify-between text-[12.5px] text-muted2">
                      <span>Comes off</span>
                      <span className="font-mono">−{moneyExact(discountValue, sym)}</span>
                    </div>
                  )}
                </div>

                <div className="px-4 py-3 border-t border-line flex items-center gap-2">
                  <span className="text-[13px] font-medium text-ink flex-1">Box price</span>
                  <Input
                    type="number"
                    min="0"
                    value={form.priceOverride !== "" ? form.priceOverride : boxPrice || ""}
                    onChange={(e) => setForm({ ...form, priceOverride: e.target.value })}
                    className="w-28 h-9 text-right font-mono"
                  />
                </div>

                <div className="px-4 py-3 border-t border-line space-y-1.5">
                  <div className="flex items-center justify-between text-[12.5px]">
                    <span className="text-muted2">Costs you</span>
                    <span className="font-mono text-muted2">{moneyExact(contentsCost, sym)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-ink">You make</span>
                    <span
                      className={cn(
                        "font-mono font-semibold",
                        belowCost ? "text-stock-low" : "text-stock-in"
                      )}
                    >
                      {moneyExact(profit, sym)}
                    </span>
                  </div>
                  {belowCost && (
                    <p className="text-[12px] text-stock-low leading-relaxed pt-1">
                      That's {moneyExact(Math.abs(profit), sym)} below what the contents
                      cost you. Fine if it's deliberate — just so you know.
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="bn">Notes</Label>
                <Input
                  id="bn"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Optional"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setBuilding(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover">
              {editingId ? "Save changes" : "Build box"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
